/**
 * AGC: Matchmaking lobby component
 * AGC: Displays current P2P queue grouped by difficulty with expandable sections
 */

import React, { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronUp, Users } from 'lucide-react'
import { buildBackendUrl } from '../utils/backendUrl'

interface QueueEntry {
  address: string
  level: number
  joinedAt: number
  isAI: boolean
}

interface QueueSnapshot {
  queue: Record<string, QueueEntry[]>
  updatedAt: number
}

interface MatchmakingLobbyProps {
  title?: string
  className?: string
  collapsedItemCount?: number
  pollIntervalMs?: number
  onJoinMatch?: (targetAddress: string, difficulty: string, level: number) => void // AGC: Callback when clicking on a queue entry
}

const DIFFICULTIES: Array<{ key: 'normal' | 'expert' | 'insane'; label: string; description: string }> = [
  { key: 'normal', label: 'Normal', description: 'Casual battles • Level gap ≤ 5' },
  { key: 'expert', label: 'Expert', description: 'Competitive battles • Level gap ≤ 10' },
  { key: 'insane', label: 'Insane', description: 'High-stakes battles • Level gap ≤ 15' }
]

const DEFAULT_POLL_INTERVAL = 5000
const DEFAULT_COLLAPSED_COUNT = 2

const shortAddress = (address: string) => {
  if (!address || address.length < 10) {
    return address || 'Unknown'
  }
  return `${address.slice(0, 6)}…${address.slice(-4)}`
}

export const MatchmakingLobby: React.FC<MatchmakingLobbyProps> = ({
  title = 'Matchmaking Queue',
  className,
  collapsedItemCount = DEFAULT_COLLAPSED_COUNT,
  pollIntervalMs = DEFAULT_POLL_INTERVAL,
  onJoinMatch // AGC: Callback for joining a specific match
}) => {
  const [queueSnapshot, setQueueSnapshot] = useState<QueueSnapshot | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true
    let pollTimer: ReturnType<typeof setInterval> | null = null

    const fetchQueue = async () => {
      try {
        if (!isMounted) {
          return
        }
        setIsLoading(true)
        const response = await fetch(buildBackendUrl('api/matchmaking/queue'))
        if (!response.ok) {
          throw new Error(`Queue request failed (${response.status})`)
        }
        const data = (await response.json()) as QueueSnapshot
        if (isMounted) {
          setQueueSnapshot(data)
          setError(null)
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err.message || 'Failed to load matchmaking queue')
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    fetchQueue()
    pollTimer = setInterval(fetchQueue, pollIntervalMs)

    return () => {
      isMounted = false
      if (pollTimer) {
        clearInterval(pollTimer)
      }
    }
  }, [pollIntervalMs])

  const queueTotals = useMemo(() => {
    if (!queueSnapshot?.queue) {
      return {
        total: 0,
        byDifficulty: DIFFICULTIES.map(({ key }) => ({ key, count: 0 }))
      }
    }

    const byDifficulty = DIFFICULTIES.map(({ key }) => {
      const entries = queueSnapshot.queue[key] || []
      return { key, count: entries.length }
    })

    const total = byDifficulty.reduce((sum, entry) => sum + entry.count, 0)

    return { total, byDifficulty }
  }, [queueSnapshot])

  const renderDifficultySection = (difficultyKey: 'normal' | 'expert' | 'insane') => {
    const entries = queueSnapshot?.queue?.[difficultyKey] || []
    if (entries.length === 0 && !isExpanded) {
      return null
    }

    const collapsedEntries = entries.slice(0, collapsedItemCount)
    const remainingCount = Math.max(entries.length - collapsedItemCount, 0)

    return (
      <div key={difficultyKey} className="bg-slate-800 border border-slate-700 rounded-lg p-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-white font-semibold text-base">
              {DIFFICULTIES.find((entry) => entry.key === difficultyKey)?.label}
            </h4>
            <p className="text-[10px] text-gray-400">
              {DIFFICULTIES.find((entry) => entry.key === difficultyKey)?.description}
            </p>
          </div>
          <div className="text-xs text-gray-300 flex items-center gap-2">
            <Users className="w-3.5 h-3.5" /> {entries.length}
          </div>
        </div>

        {entries.length === 0 ? (
          <p className="text-xs text-gray-500 mt-3">No players waiting in this queue.</p>
        ) : (
          <div className="mt-3 space-y-1.5">
            {(isExpanded ? entries : collapsedEntries).map((entry) => (
              <div 
                key={`${entry.address}-${entry.joinedAt}`} 
                className="bg-slate-900 rounded-md px-2.5 py-1.5 flex items-center justify-between cursor-pointer hover:bg-slate-800 transition-colors"
                onClick={() => {
                  console.log('🎯 MatchmakingLobby: Clicked on entry', { address: entry.address, difficulty: difficultyKey, level: entry.level })
                  console.log('🎯 MatchmakingLobby: onJoinMatch callback exists?', !!onJoinMatch)
                  if (onJoinMatch) {
                    onJoinMatch(entry.address, difficultyKey, entry.level)
                  } else {
                    console.warn('⚠️ MatchmakingLobby: onJoinMatch callback not provided')
                  }
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    console.log('🎯 MatchmakingLobby: Keyboard join triggered')
                    onJoinMatch?.(entry.address, difficultyKey, entry.level)
                  }
                }}
              >
                <div>
                  <div className="text-white text-sm font-medium flex items-center gap-2">
                    <Users className="w-3.5 h-3.5 text-purple-300" />
                    {shortAddress(entry.address)}
                  </div>
                  <div className="text-[10px] text-gray-500">
                    Level {entry.level} · Waiting {formatWaitTime(entry.joinedAt)}
                  </div>
                </div>
              </div>
            ))}

            {!isExpanded && remainingCount > 0 && (
              <div className="text-[10px] text-gray-400">+{remainingCount} more waiting…</div>
            )}
          </div>
        )}
      </div>
    )
  }

  const formatWaitTime = (joinedAt: number) => {
    const now = Date.now()
    const diffMs = Math.max(now - joinedAt, 0)
    const seconds = Math.floor(diffMs / 1000)
    const minutes = Math.floor(seconds / 60)

    if (minutes >= 1) {
      return `${minutes}m ${seconds % 60}s`
    }

    return `${seconds}s`
  }

  return (
    <div className={[
      'bg-slate-700 border border-slate-600 rounded-lg p-4',
      className || ''
    ].filter(Boolean).join(' ')}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          <p className="text-xs text-gray-400">Live queue snapshot · Updated {queueSnapshot ? formatWaitTime(queueSnapshot.updatedAt) : '—'} ago</p>
        </div>
        <button
          onClick={() => setIsExpanded((prev) => !prev)}
          className="rounded-full p-2 border border-slate-700 text-slate-200 hover:bg-slate-700 transition-colors"
          aria-label={isExpanded ? 'Collapse queue' : 'Expand queue'}
        >
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      <div className="mt-3 flex gap-4 text-xs text-gray-300">
        <div className="flex items-center gap-1">
          <Users className="w-3 h-3" /> Total: {queueTotals.total}
        </div>
        {isLoading && <span className="text-blue-300">Refreshing…</span>}
        {error && <span className="text-red-400">{error}</span>}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3">
        {DIFFICULTIES.map(({ key }) => renderDifficultySection(key))}
      </div>
    </div>
  )
}
